// Questionnaire result viewer — 问卷结果
//
// Extracted from KpiV2.js so the 客户 CRM modal on /profiles can show the same
// 人生合伙人画像 / 性格测评 results the ticket timeline shows, without pulling in
// the whole KPIv2 page bundle. KpiV2 imports it from here; there is one
// implementation and both places stay in step.
//
// Handles all three shapes the questionnaires produce:
//   profile_preferences (人生合伙人画像) → the shared 4-column PreferencesTable
//   love_language (爱的五种语言)        → an A–E tally plus the dominant type
//   everything else                     → a plain 题目 / 回答 table

import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { kpiV2API } from '../../services/api';
import PreferencesTable from './PreferencesTable';

export default function QuestionnaireResultsModal({ submissionId, profileId = null, isZh, onClose }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // From the CRM card the caller may not be on the sending ticket, so
        // read through the profile-scoped route when a profileId is given.
        const r = profileId
          ? await kpiV2API.getProfileQuestionnaire(profileId, submissionId)
          : await kpiV2API.getQuestionnaireSubmission(submissionId);
        if (alive) setData(r.data || null);
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || (isZh ? '加载失败' : 'Failed to load'));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [submissionId, profileId, isZh]);

  const submission = data?.submission || null;
  const questions = Array.isArray(data?.questions) ? data.questions : [];
  const responses = submission?.responses || {};
  const preferenceResults = Array.isArray(data?.preference_results) ? data.preference_results : [];
  // profile_preferences questionnaires (人生合伙人画像 etc.) carry their
  // answers as { ideal, acceptable, bottom_line } per custom field and
  // should render through the shared PreferencesTable so all three tiers
  // line up the same way the 择偶要求 modal does it elsewhere.
  const isPreferences = preferenceResults.length > 0
    || submission?.questionnaire_type === 'profile_preferences';

  // 爱的五种语言 — tally how many times each option (A–E) was chosen across all
  // questions and surface the dominant love language, like the client-facing
  // result screen.
  const isLoveLanguage = submission?.questionnaire_type === 'love_language';
  const optsOf = (q) => {
    const o = q?.options;
    if (Array.isArray(o)) return o;
    if (typeof o === 'string') { try { return JSON.parse(o); } catch { return []; } }
    return [];
  };
  let llRows = [], llTop = null;
  if (isLoveLanguage && questions.length) {
    const LETTERS = 'ABCDEFGH'.split('');
    const baseOpts = optsOf(questions[0]);
    const counts = baseOpts.map(() => 0);
    questions.forEach((q) => {
      const chosen = responses[q.id];
      if (chosen == null || chosen === '') return;
      const opts = optsOf(q);
      const idx = opts.findIndex(o => o && (o.label === chosen || o.label_zh === chosen || o === chosen));
      if (idx >= 0 && idx < counts.length) counts[idx] += 1;
    });
    llRows = baseOpts.map((o, i) => ({
      letter: LETTERS[i] || String(i + 1),
      label: isZh ? (o.label_zh || o.label || '') : (o.label || o.label_zh || ''),
      count: counts[i],
    }));
    const maxCount = Math.max(0, ...counts);
    llTop = maxCount > 0 ? llRows.find(r => r.count === maxCount) : null;
  }

  // Each question stores its options as JSON. For multiple_choice / checkbox
  // types we let the answer fall through as-is; for rating we render N/max.
  const renderAnswer = (q) => {
    const raw = responses[q.id];
    if (raw == null || raw === '') return <span style={{ color: '#C9C3BA' }}>—</span>;
    if (q.question_type === 'rating' || q.question_type === 'scale') {
      const max = q.max_rating || 5;
      return <span><strong style={{ color: '#111111' }}>{raw}</strong> <span style={{ color: '#6E685F' }}>/ {max}</span></span>;
    }
    if (Array.isArray(raw)) return raw.join(isZh ? '、' : ', ');
    if (typeof raw === 'object') return <pre style={{ margin: 0, fontSize: 11, fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}>{JSON.stringify(raw, null, 2)}</pre>;
    return String(raw);
  };

  // Split off the leading "N." or "N、" + the dimension name vs. description.
  // We always render dimension and description on separate lines so the
  // table feels structured even when questions are long sentences.
  const splitQuestionText = (text) => {
    if (!text) return { head: '', tail: '' };
    const s = String(text).trim();
    // Split on first " - " or " — " or "：" or ":"
    const m = s.match(/^([^\-—：:]+?)\s*[\-—：:]\s*(.+)$/);
    if (m) return { head: m[1].trim(), tail: m[2].trim() };
    return { head: s, tail: '' };
  };

  const title = isZh
    ? (submission?.questionnaire_title_zh || submission?.questionnaire_title || '问卷结果')
    : (submission?.questionnaire_title || submission?.questionnaire_title_zh || 'Questionnaire Results');

  const subtitleParts = [];
  if (submission?.sent_to_name) subtitleParts.push(submission.sent_to_name);
  if (submission?.submitted_at) subtitleParts.push(new Date(submission.submitted_at).toLocaleDateString(isZh ? 'zh-CN' : 'en-US'));

  const tdStyle = {
    padding: '10px 12px', fontSize: 13, color: '#3A3835',
    border: '1px solid #E4DFD7', verticalAlign: 'top', lineHeight: 1.5,
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#FFFDF9', borderRadius: 2, width: 'min(900px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'none', position: 'relative', fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif" }}>
        <button type="button" onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', color: '#6E685F', cursor: 'pointer', zIndex: 1 }}>
          <X size={20} />
        </button>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6E685F' }}>
            <RefreshCw size={22} style={{ animation: 'spin 1s linear infinite', marginBottom: 10 }} />
            <div style={{ fontSize: 13 }}>{t('加载中…', 'Loading…')}</div>
          </div>
        ) : error ? (
          <div style={{ padding: '40px 24px', color: '#9E2B25', fontSize: 13, textAlign: 'center' }}>{error}</div>
        ) : !submission ? (
          <div style={{ padding: '40px 24px', color: '#6E685F', fontSize: 13, textAlign: 'center' }}>{t('未找到记录', 'Not found')}</div>
        ) : (
          <>
            {/* Centered serif title + "<name> - <date>" subtitle, matching
                the 择偶要求 modal header style. */}
            <div style={{ textAlign: 'center', padding: '28px 24px 8px' }}>
              <h3 style={{ color: '#111111', fontSize: 26, fontWeight: 500, margin: 0, fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif" }}>
                {title}
              </h3>
              {subtitleParts.length > 0 && (
                <div style={{ fontSize: 13, color: '#6E685F', marginTop: 6 }}>
                  {subtitleParts.join(' - ')}
                </div>
              )}
              {submission.average_score != null && (
                <div style={{ fontSize: 12, color: '#6E685F', marginTop: 4 }}>
                  {t('平均分', 'Avg score')}：<strong style={{ color: '#111111' }}>{submission.average_score}</strong>
                </div>
              )}
            </div>

            <div style={{ padding: '12px 16px 20px', overflow: 'auto', flex: 1 }}>
              {isPreferences ? (
                preferenceResults.length === 0 ? (
                  <div style={{ color: '#6E685F', fontSize: 13, textAlign: 'center', padding: 30 }}>
                    {t('(暂无作答)', '(no responses yet)')}
                  </div>
                ) : (
                  <PreferencesTable preferenceResults={preferenceResults} isZh={isZh} maxHeight="60vh" />
                )
              ) : isLoveLanguage ? (
                <div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #E4DFD7' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6E685F', width: 56 }}>{t('选项', 'Option')}</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6E685F' }}>{t('对应爱语', 'Love language')}</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#6E685F', width: 84 }}>{t('选中次数', 'Times selected')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {llRows.map((r) => {
                        const top = llTop && r.letter === llTop.letter && r.count > 0;
                        return (
                          <tr key={r.letter} style={{ borderBottom: '1px solid #EFEAE2', background: top ? '#F3EADB' : '#FFFDF9', borderRadius: top ? 10 : 0 }}>
                            <td style={{ padding: '14px 10px' }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 34, height: 34, borderRadius: '50%', fontSize: 14, fontWeight: 700,
                                background: top ? '#111111' : '#EFEAE2', color: top ? '#FFFDF9' : '#6E685F',
                              }}>{r.letter}</span>
                            </td>
                            <td style={{ padding: '14px 10px', fontSize: 14, lineHeight: 1.5, fontWeight: top ? 700 : 500, color: top ? '#111111' : '#3A3835' }}>{r.label}</td>
                            <td style={{ padding: '14px 10px', textAlign: 'right', fontSize: top ? 22 : 18, fontWeight: 700, color: top ? '#111111' : '#D6D0C7' }}>{r.count}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {llTop && (
                    <div style={{ marginTop: 22, padding: '18px 20px', borderRadius: 2, textAlign: 'center', background: '#111111', color: '#FFFDF9' }}>
                      <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 6 }}>{t('您的主要爱语', 'Your primary love language')}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.45 }}>{llTop.label}</div>
                    </div>
                  )}
                </div>
              ) : questions.length === 0 ? (
                <div style={{ color: '#6E685F', fontSize: 13, textAlign: 'center', padding: 30 }}>
                  {t('(此问卷暂无题目)', '(no questions)')}
                </div>
              ) : (
                <table style={{
                  width: '100%', borderCollapse: 'collapse', fontSize: 13,
                  border: '1px solid #E4DFD7',
                }}>
                  <thead>
                    <tr style={{ background: '#F6F3EE' }}>
                      <th style={{
                        padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700,
                        color: '#3A3835', border: '1px solid #E4DFD7', width: '40%', minWidth: 200,
                      }}>{t('维度', 'Dimension')}</th>
                      <th style={{
                        padding: '10px 12px', textAlign: 'left', fontSize: 12, fontWeight: 700,
                        color: '#3A3835', border: '1px solid #E4DFD7',
                      }}>{t('答案', 'Answer')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q) => {
                      const qText = isZh ? (q.question_text_zh || q.question_text) : (q.question_text || q.question_text_zh);
                      const { head, tail } = splitQuestionText(qText);
                      return (
                        <tr key={q.id}>
                          <td style={{
                            ...tdStyle,
                            fontWeight: 600, color: '#111111',
                            background: '#F6F3EE',
                          }}>
                            <div>{head}{q.is_required ? <span style={{ color: '#9E2B25', marginLeft: 4, fontWeight: 400 }}>*</span> : null}</div>
                            {tail && (
                              <div style={{ fontSize: 11, color: '#6E685F', fontWeight: 400, marginTop: 3, lineHeight: 1.4 }}>
                                {tail}
                              </div>
                            )}
                          </td>
                          <td style={tdStyle}>{renderAnswer(q)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ padding: '10px 18px 16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose}
                style={{ padding: '6px 16px', borderRadius: 2, border: '1px solid #E4DFD7', background: '#FFFDF9', color: '#3A3835', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {t('关闭', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
